import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { RootState } from '../reducers';
import { connect, useSelector } from 'react-redux';
import {
  DEFAULT_PAGINATION_LIMIT,
  ENTITY_TYPE,
  getActorRoleString,
  getFormattedDate,
} from './utils';
import { ReactComponent as NavigateBack } from '../assets/navigate_back.svg';
import { ReactComponent as Alert } from '../assets/action_history_alert.svg';
import { ReactComponent as ArrowLeft } from '../assets/arrow_left.svg';
import { ReactComponent as ArrowRight } from '../assets/arrow_right.svg';
import Dropdown, { DropdownOption } from './Dropdown';
import {
  fetchAccountHistoryDetails,
  fetchActionHistory,
  navigateBackActionHistoryList,
} from '../actions/orderActions';
import moment from 'moment';
import {
  FormattedMessage,
  injectIntl,
  WrappedComponentProps,
} from 'react-intl';
import { bindActionCreators } from 'redux';
import {
  bundleDetailsSelector,
  orderDetailsSelector,
} from '../selectors/bundleSelectors';
import {
  getOrderTypeFromId,
  getProductFromOrderType,
} from '../helpers/product.helpers';

export interface ActionHistoryItem {
  entityId: number;
  entityType: string;
  id: number;
  timestamp: number;
  action: string;
  role: string;
}

type DetailsProp = {
  actionHistoryList: ActionHistoryItem[];
  entityType: ENTITY_TYPE;
  selectedDomainName: string;
  currentEmail: string;
  token: string;
  domainId: string;
  region: string;
  userRole: string;
  actionHistoryPaginationProps: {
    currentPage: number;
    numberOfRecordsVisible: number;
  };
  actionHistoryEntityType: (typeof ENTITY_TYPE)[keyof typeof ENTITY_TYPE];
  fetchActionHistoryDetailsInProgress: boolean;
  isFetchActionHistoryPaginationInProgress: boolean;
  currentOrderSelected: any;
  fetchActionHistory: (
    domainId: string,
    accountIds: number[] | null,
    entityType: string,
    token: string,
    limit: number,
    lastId: number
  ) => void;
  navigateBackActionHistoryList: () => void;
  fetchAccountHistoryDetails: (
    actionType: string,
    actionId: number,
    bundleId: number,
    authToken: string,
    region: string,
    paginationProps: { currentPage: number; numberOfRecordsVisible: number }
  ) => any;
} & WrappedComponentProps;

type Props = DetailsProp;

const recordsPerPageDropdownItems: DropdownOption[] = [
  { label: '5', value: 5 },
  { label: '10', value: 10 },
  { label: '20', value: 20 },
];

const ActionHistoryList: React.FC<Props> = (props) => {
  const {
    actionHistoryList,
    actionHistoryPaginationProps,
    currentEmail,
    entityType,
    intl,
    navigateBackActionHistoryList,
    fetchAccountHistoryDetails,
    fetchActionHistory,
    region,
    selectedDomainName,
    token,
    userRole,
    currentOrderSelected,
    isFetchActionHistoryPaginationInProgress,
    fetchActionHistoryDetailsInProgress,
  } = props;

  const [currentPage, setCurrentPage] = useState<number>(
    actionHistoryPaginationProps.currentPage || 1
  );
  const [itemIdClicked, setItemIdClicked] = useState<number>(-1);
  const [numberOfRecordsVisible, setNumberOfRecordsVisible] = useState<number>(
    actionHistoryPaginationProps.numberOfRecordsVisible ||
      DEFAULT_PAGINATION_LIMIT
  );

  const orders = useSelector(orderDetailsSelector);
  const { id } = useSelector(bundleDetailsSelector) || {};

  const topDivRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef<boolean>(true);

  useEffect(() => {
    topDivRef.current?.scrollIntoView();
  }, []);

  useEffect(() => {
    // no pagination for account action history
    if (entityType !== ENTITY_TYPE.ORDER) return;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      // no need to load new chunk on first render
      return;
    }

    loadNewChunk();
  }, [currentPage, numberOfRecordsVisible, entityType]);

  const updateRecordsPerPage = (item: DropdownOption) => {
    if (item.value === numberOfRecordsVisible) return;
    setCurrentPage(1);
    setNumberOfRecordsVisible(item.value);
  };

  const getAccountIdsForCurrentOrder = useCallback((): number[] | null => {
    const accounts = currentOrderSelected.accounts;
    if (accounts) {
      return accounts.map((account: { id: number }) => account.id);
    }
    return null;
  }, [currentOrderSelected]);

  const loadNewChunk = () => {
    const recordsPresent =
      actionHistoryList.length > numberOfRecordsVisible * currentPage + 1;
    if (recordsPresent) return;
    const lastItem = actionHistoryList[actionHistoryList.length - 1];
    if (lastItem) {
      fetchActionHistory(
        id,
        getAccountIdsForCurrentOrder(),
        entityType,
        token,
        numberOfRecordsVisible,
        lastItem.id
      );
    }
  };

  const handleActionItemClick = useCallback(
    (item: ActionHistoryItem) => {
      setItemIdClicked(item.id);
      fetchAccountHistoryDetails(
        item.action,
        item.id,
        id,
        token,
        currentOrderSelected.region,
        { currentPage, numberOfRecordsVisible }
      );
    },
    [
      currentPage,
      numberOfRecordsVisible,
      currentOrderSelected,
      fetchAccountHistoryDetails,
      token,
    ]
  );

  const loadPreviousPage = () => {
    if (currentPage <= 1) return;
    setCurrentPage((prevPage) => prevPage - 1);
  };

  const loadNextPage = (shouldAllowNextLoad: boolean) => {
    if (!shouldAllowNextLoad) return;
    setCurrentPage((prevPage) => prevPage + 1);
  };

  if (!id) {
    return null;
  }

  // Rendering part
  const domainName = selectedDomainName;
  const actionHistoryText = intl.formatMessage({ id: 'action_history' });
  const heading =
    entityType === ENTITY_TYPE.ORDER
      ? `${domainName} : ${actionHistoryText}`
      : `${currentEmail} : ${actionHistoryText}`;

  let indexOfAlertRow = -1;
  const isDomainActionLog = entityType === ENTITY_TYPE.ORDER;
  const startPoint = isDomainActionLog
    ? numberOfRecordsVisible * (currentPage - 1)
    : 0;
  const endPoint = isDomainActionLog
    ? numberOfRecordsVisible * currentPage
    : actionHistoryList.length;
  const slicedList = (actionHistoryList || []).slice(startPoint, endPoint);

  const historyRows = slicedList.map((item, i) => {
    const columns = [];
    const index = item.action.indexOf(':');
    let actionHyperlink =
      index === -1 ? item.action : item.action.substring(0, index + 1);
    const actionText = index === -1 ? '' : item.action.substring(index + 1);
    const showSpinner =
      fetchActionHistoryDetailsInProgress && itemIdClicked === item.id;
    const formattedDate = getFormattedDate(item.timestamp);
    if (
      moment(formattedDate).isBefore('27 Aug 2020') &&
      indexOfAlertRow === -1
    ) {
      indexOfAlertRow = i;
    }
    columns.push(
      <td className="action-history-time" key={`time-${item.id}`}>
        {formattedDate}
      </td>
    );
    columns.push(
      <td className="action-history-info" key={`info-${item.id}`}>
        <div className="action-link-wrapper">
          <div className="action-link-div">
            <div
              className="action-detail-link"
              onClick={() => handleActionItemClick(item)}
            >
              {actionHyperlink}
            </div>
            {actionText}
          </div>
          <div className="spinner-action-continer">
            {showSpinner ? <div className="spinner-action" /> : null}
          </div>
        </div>
      </td>
    );
    entityType !== ENTITY_TYPE.ACCOUNT &&
      columns.push(
        <td className="action-history-info" key={`product-${item.id}`}>
          {item.entityType === 'BUNDLE'
            ? 'Bundle'
            : getProductFromOrderType(
                getOrderTypeFromId(orders, item.entityId)
              )}
        </td>
      );
    columns.push(
      <td className="action-history-info" key={`role-${item.id}`}>
        {getActorRoleString(item.role, userRole)}
      </td>
    );
    return <tr key={item.id}>{columns}</tr>;
  });

  // Append spinner row if pagination is in progress
  if (isFetchActionHistoryPaginationInProgress) {
    historyRows.push(
      <tr key="pagination-spinner">
        <td colSpan={4} className="spinner-action-continer pagination-spinner">
          <div className="spinner-action" />
        </td>
      </tr>
    );
  }

  if (!historyRows.length) {
    const entityText = entityType === ENTITY_TYPE.ORDER ? 'order' : 'account';
    historyRows.push(
      <tr key="no-history">
        <td colSpan={4} className="no-action-history-list">
          <FormattedMessage
            id={currentPage ? 'end_of_action' : 'no_action_found'}
            values={{ entity: entityText }}
          />
        </td>
      </tr>
    );
  }

  if (indexOfAlertRow !== -1) {
    const alertRow = (
      <tr key="alert-row">
        <td colSpan={4} className="action-history-alert">
          <div className="flex">
            <Alert />
            <span className="action-history-alert-text">
              <FormattedMessage id="action_history_alert_text" />
            </span>
          </div>
        </td>
      </tr>
    );
    historyRows.splice(indexOfAlertRow, 0, alertRow);
  }

  const itemSelectedIndex = recordsPerPageDropdownItems.findIndex(
    (dropDownOption) => dropDownOption.value === numberOfRecordsVisible
  );
  const shouldAllowNextLoad = slicedList.length === numberOfRecordsVisible;
  const shouldAllowPrevLoad = currentPage > 1;

  return (
    <div
      className="action-history-container pagination-details-container"
      ref={topDivRef}
    >
      <div className="action-history-heading-container">
        <NavigateBack
          className="action-history-navigate-back"
          onClick={navigateBackActionHistoryList}
        />
        <div className="action-history-heading">{heading}</div>
      </div>
      <table className="pagination-details">
        <thead>
          <tr>
            <th className="action-history-time-heading">
              <FormattedMessage id="action_history_date_time" />
            </th>
            <th className="action-history-info">
              <FormattedMessage id="action" />
            </th>
            {entityType !== ENTITY_TYPE.ACCOUNT && (
              <th className="action-history-info">
                <FormattedMessage id="action_history_order" />
              </th>
            )}
            <th className="action-history-info">
              <FormattedMessage id="action_history_role" />
            </th>
          </tr>
        </thead>
        <tbody>{historyRows}</tbody>
      </table>
      {entityType === ENTITY_TYPE.ORDER && (
        <div className="page-div">
          <span className="page-text">
            <FormattedMessage id="label_records_per_page" />
          </span>
          <Dropdown
            mainClass="records-div"
            headerClass="records-dd-header"
            headerTitleClass="records-dd-header-title"
            listContainerClass="records-dd-list-container"
            dropDownItemClass="records-dd-menu-item"
            onItemClick={updateRecordsPerPage}
            placeholderText=""
            placeholderClass=""
            list={recordsPerPageDropdownItems}
            defaultItemSelectedIndexes={[itemSelectedIndex]}
            shouldAllowMultiSelect={false}
          />
          <span className="page-text">
            <FormattedMessage
              id="pagination_current_page"
              values={{ pageNo: currentPage }}
            />
          </span>
          <div className="page-buttons">
            <ArrowLeft
              className={`prev-page ${!shouldAllowPrevLoad ? 'disabled-arrow' : ''}`}
              onClick={() => loadPreviousPage()}
            />
            <ArrowRight
              className={`next-page ${!shouldAllowNextLoad ? 'disabled-arrow' : ''}`}
              onClick={() => loadNextPage(shouldAllowNextLoad)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const orders = state.orders as any;
  return {
    region: orders.currentOrderSelected.region,
    actionHistoryList: orders.actionHistoryList,
    entityType: orders.actionHistoryEntityType,
    selectedDomainName: orders.selectedDomainName,
    currentEmail: orders.currentEmail,
    actionHistoryPaginationProps: orders.actionHistoryPaginationProps,
    isFetchActionHistoryPaginationInProgress:
      orders.isFetchActionHistoryPaginationInProgress,
    fetchActionHistoryDetailsInProgress:
      orders.fetchActionHistoryDetailsInProgress,
    token: state.auth.token,
    userRole: state.auth.userRole,
    currentOrderSelected: orders.currentOrderSelected,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    ...bindActionCreators(
      {
        navigateBackActionHistoryList,
        fetchAccountHistoryDetails,
        fetchActionHistory,
      },
      dispatch
    ),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(ActionHistoryList));
